import React, { useState } from 'react';
import { useParams } from 'next/navigation';
import CartDrawer from 'components/commercetools-ui/atoms/cart-drawer';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import { UtilityLink } from 'components/commercetools-ui/organisms/header/types';
import Slideout, { MenuState } from 'components/commercetools-ui/organisms/header/utility-section/components/slide-out';
import { useFormat } from 'helpers/hooks/useFormat';
import { Cart, Discount, LineItem as CartLineItem } from 'types/entity/cart';
import { Wishlist, LineItem as WishlistLineItem } from 'types/entity/wishlist';
import { Reference } from 'types/reference';
import AccountButton from './components/account-button';
export interface CategoriesLink {
  name: string;
  reference: Reference;
}
export interface Props {
  cart?: Cart;
  isEmpty?: boolean;
  onApplyDiscountCode?: (code: string) => Promise<void>;
  onRemoveDiscountCode?: (discount: Discount) => Promise<void>;
  totalCartItems?: number;
  totalWishlistItems?: number;
  onRemoveItem(itemId: string): Promise<void>;
  onUpdateItem(itemId: string, quantity: number): Promise<void>;
  OnMoveToWishlist(lineItem: CartLineItem): Promise<void>;
  wishlist?: Wishlist;
  onRemoveFromWishlist?: (lineItemId: string) => Promise<void>;
  onMoveToCart?: (lineItem: WishlistLineItem) => Promise<void>;
  onClearWishlist?: () => Promise<void>;
  emptyCartTitle: string;
  emptyCartSubtitle: string;
  emptyCartImage: ImageProps;
  emptyCartCategories: CategoriesLink[];
  emptyWishlistTitle: string;
  emptyWishlistSubtitle: string;
  emptyWishlistImage: ImageProps;
  emptyWishlistCategories: CategoriesLink[];
  utilityLinks: UtilityLink[];
}

const UtilitySection: React.FC<Props> = ({
  cart,
  isEmpty,
  onApplyDiscountCode,
  onRemoveDiscountCode,
  onRemoveItem,
  onUpdateItem,
  OnMoveToWishlist,
  totalCartItems = 0,
  totalWishlistItems = 0,
  wishlist,
  onRemoveFromWishlist,
  onMoveToCart,
  onClearWishlist,
  emptyCartTitle,
  emptyCartSubtitle,
  emptyCartImage,
  emptyCartCategories,
  emptyWishlistTitle,
  emptyWishlistSubtitle,
  emptyWishlistImage,
  emptyWishlistCategories,
  utilityLinks,
}) => {
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const { locale } = useParams();

  const [menuState, setMenuState] = useState<MenuState>();
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const onWishlistClicked = () => {
    setIsDrawerOpen(true);
    setMenuState('wishlist');
  };

  const onCartClicked = () => {
    setIsDrawerOpen(true);
    setMenuState('cart');
  };

  const renderUtilityLinkContent = (utilityLink: any, onCartClicked: () => void, onWishlistClicked: () => void) => {
    let content;
    const localeKey = locale.replace(/-/g, '_');
    const headerBtnStyle =
      'group relative flex size-40 cursor-pointer flex-row items-center justify-center border-secondary-black before:absolute before:left-0 before:top-0 before:size-full before:rounded-sm before:outline-2 before:outline-orange-500 before:content-[""] hover:bg-orange-100 hover:before:outline';
    const imageStyle = 'size-24 text-secondary-black';
    const iconStyle =
      'group-hover:border-orange-100 group-hover:text-orange-100 absolute -right-4 -top-4 z-10 flex size-26 items-center justify-center rounded-full border-2 border-white bg-blue-500 text-10 text-white';

    switch (utilityLink.link?.link) {
      case '/cart':
        content = (
          <div
            title={formatCartMessage({ id: 'myCart', defaultMessage: 'My Cart' })}
            className={headerBtnStyle}
            onClick={onCartClicked}
          >
            {totalCartItems > 0 && <div className={iconStyle}>{totalCartItems}</div>}

            <Image
              className={imageStyle}
              src={utilityLink.icon?.media?.file}
              alt={
                utilityLink.icon?.media?.alt !== ''
                  ? utilityLink.icon?.media?.alt
                  : utilityLink.icon?.title?.[localeKey]
              }
              title={utilityLink.icon?.title?.[localeKey]}
            />
          </div>
        );
        break;

      case '/account':
        content = <AccountButton className={`${headerBtnStyle}`} utilityLink={utilityLink} imageStyle={imageStyle} />;
        break;

      case '/wishlist':
        content = (
          <div
            title={formatCartMessage({ id: 'myWishlist', defaultMessage: 'Save For Later' })}
            className={headerBtnStyle}
            onClick={onWishlistClicked}
          >
            {totalWishlistItems > 0 && <div className={iconStyle}>{totalWishlistItems}</div>}
            <Image
              className={imageStyle}
              src={utilityLink.icon?.media?.file}
              alt={
                utilityLink.icon?.media?.alt !== ''
                  ? utilityLink.icon?.media?.alt
                  : utilityLink.icon?.title?.[localeKey]
              }
              title={utilityLink.icon?.title?.[localeKey]}
            />
          </div>
        );
        break;

      default:
        content = (
          <Link link={utilityLink.link} title={utilityLink.icon?.title?.[localeKey]}>
            <div className={`${headerBtnStyle} hidden sm:flex`}>
              <Image
                className={imageStyle}
                src={utilityLink.icon?.media?.file}
                alt={
                  utilityLink.icon?.media?.alt !== ''
                    ? utilityLink.icon?.media?.alt
                    : utilityLink.icon?.title?.[localeKey]
                }
                title={utilityLink.icon?.title?.[localeKey]}
              />
            </div>
          </Link>
        );
    }

    return content;
  };
  return (
    <div className="relative flex h-full items-center">
      <div className="flex justify-center gap-4">
        {utilityLinks?.map((utilityLink, i) => (
          <div key={utilityLink?.link?.title || '' + i}>
            {renderUtilityLinkContent(utilityLink, onCartClicked, onWishlistClicked)}
          </div>
        ))}
      </div>

      <CartDrawer
        isOpen={isDrawerOpen}
        direction="right"
        className={`right-0 top-0 bg-white md:top-87 md:translate-y-40 xl:top-25`}
        onClose={() => setIsDrawerOpen(false)}
      >
        <Slideout
          cart={cart}
          isEmpty={isEmpty}
          onApplyDiscountCode={onApplyDiscountCode}
          onRemoveDiscountCode={onRemoveDiscountCode}
          onRemoveItem={onRemoveItem}
          onUpdateItem={onUpdateItem}
          OnMoveToWishlist={OnMoveToWishlist}
          wishlist={wishlist}
          onRemoveFromWishlist={onRemoveFromWishlist}
          onMoveToCart={onMoveToCart}
          onClearWishlist={onClearWishlist}
          state={menuState}
          onClose={() => setIsDrawerOpen(false)}
          changeState={(newState) => setMenuState(newState)}
          emptyCartTitle={emptyCartTitle}
          emptyCartSubtitle={emptyCartSubtitle}
          emptyCartImage={emptyCartImage}
          emptyCartCategories={emptyCartCategories}
          emptyWishlistTitle={emptyWishlistTitle}
          emptyWishlistSubtitle={emptyWishlistSubtitle}
          emptyWishlistImage={emptyWishlistImage}
          emptyWishlistCategories={emptyWishlistCategories}
          totalCartItems={totalCartItems}
          totalWishlistItems={totalWishlistItems}
        />
      </CartDrawer>
    </div>
  );
};

export default UtilitySection;
